import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';

const RolePageAccessLayer = () => {
    const [accesses, setAccesses] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedRoleId, setSelectedRoleId] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [initializedRoles, setInitializedRoles] = useState(new Set());
    
    // Create page modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createFormData, setCreateFormData] = useState({
        role_id: '',
        page_route: '',
        menu_item: '',
        menu_label: '',
        can_access: true,
        show_in_menu: true,
        menu_order: 0,
    });
    const [createError, setCreateError] = useState('');
    const [createValidationErrors, setCreateValidationErrors] = useState({});
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        fetchRoles();
    }, []);

    useEffect(() => {
        if (selectedRoleId) {
            fetchAccesses();
        }
    }, [selectedRoleId]);

    // Initialize page access when role is selected and has no pages (only once per role)
    useEffect(() => {
        if (selectedRoleId && accesses.length === 0 && !loading && !initializedRoles.has(selectedRoleId)) {
            // Auto-initialize only if this role hasn't been initialized yet
            const initialize = async () => {
                await initializePageAccess();
                setInitializedRoles(prev => new Set(prev).add(selectedRoleId));
            };
            initialize();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRoleId, accesses.length, loading]);

    const fetchRoles = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/roles`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (data.success) {
                setRoles(data.data);
                // Set default role (admin)
                const adminRole = data.data.find(r => r.slug === 'admin');
                if (adminRole) {
                    setSelectedRoleId(adminRole.id.toString());
                } else if (data.data.length > 0) {
                    setSelectedRoleId(data.data[0].id.toString());
                }
            }
        } catch (err) {
            console.error('Failed to fetch roles:', err);
        }
    };

    const fetchAccesses = async () => {
        try {
            setLoading(true);
            setError('');

            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            if (!apiUrl || !token) {
                throw new Error('Missing API URL or authentication token');
            }

            const response = await fetch(`${apiUrl}/role-page-access?role_id=${selectedRoleId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error('Unauthorized. Only super admin can access this page.');
                }
                throw new Error(`Failed to fetch role page access: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setAccesses(data.data);
            } else {
                throw new Error(data.message || 'Failed to fetch role page access');
            }
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setLoading(false);
        }
    };

    const initializePageAccess = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            if (!apiUrl || !token || !selectedRoleId) {
                return;
            }

            const response = await fetch(`${apiUrl}/role-page-access/initialize/${selectedRoleId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (data.success) {
                // Refresh the access list
                fetchAccesses();
            }
        } catch (err) {
            console.error('Failed to initialize page access:', err);
        }
    };

    const handleToggle = async (id, field, currentValue) => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/role-page-access/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    [field]: !currentValue
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to update access');
            }

            const data = await response.json();

            if (data.success) {
                // Update local state
                setAccesses(accesses.map(access => 
                    access.id === id 
                        ? { ...access, [field]: !currentValue }
                        : access
                ));
            }
        } catch (err) {
            console.error(err);
            alert('Failed to update access. Please try again.');
        }
    };

    const handleBulkUpdate = async () => {
        try {
            setIsSaving(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const bulkData = accesses.map(access => ({
                id: access.id,
                can_access: access.can_access,
                show_in_menu: access.show_in_menu,
            }));

            const response = await fetch(`${apiUrl}/role-page-access/bulk-update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    accesses: bulkData
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to update access');
            }

            const data = await response.json();

            if (data.success) {
                alert('Role page access updated successfully!');
                fetchAccesses();
            }
        } catch (err) {
            console.error(err);
            alert('Failed to update access. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCreateInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Clear validation error for this field
        if (createValidationErrors[name]) {
            setCreateValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
        
        // Handle auto-generation for page_route
        if (name === 'page_route') {
            const newValue = value;
            setCreateFormData(prev => {
                const updated = {
                    ...prev,
                    [name]: newValue
                };
                
                // Auto-generate menu_item from page_route if menu_item is empty
                if (!prev.menu_item || prev.menu_item === '') {
                    const menuItem = newValue.replace(/^\//, '').replace(/\//g, '-').toLowerCase();
                    updated.menu_item = menuItem;
                }
                
                // Auto-generate menu_label from page_route if menu_label is empty
                if (!prev.menu_label || prev.menu_label === '') {
                    const menuLabel = newValue
                        .replace(/^\//, '')
                        .split('/')
                        .filter(word => word.length > 0)
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');
                    updated.menu_label = menuLabel;
                }
                
                return updated;
            });
        } else {
            // For other fields, just update normally
            setCreateFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        setIsCreating(true);
        setCreateError('');
        setCreateValidationErrors({});

        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            // Get role slug for backward compatibility
            const selectedRole = roles.find(r => r.id.toString() === createFormData.role_id.toString());
            const submitData = {
                ...createFormData,
                role: selectedRole ? selectedRole.slug : '',
                menu_order: parseInt(createFormData.menu_order) || 0,
            };

            const response = await fetch(`${apiUrl}/role-page-access`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(submitData),
            });

            const data = await response.json();

            if (data.success) {
                setShowCreateModal(false);
                // Refresh the access list if it's for the currently selected role
                if (createFormData.role_id === selectedRoleId) {
                    fetchAccesses();
                }
                // Reset form
                setCreateFormData({
                    role_id: selectedRoleId || '',
                    page_route: '',
                    menu_item: '',
                    menu_label: '',
                    can_access: true,
                    show_in_menu: true,
                    menu_order: 0,
                });
            } else {
                if (data.errors) {
                    setCreateValidationErrors(data.errors);
                } else {
                    setCreateError(data.message || 'Failed to create page access');
                }
            }
        } catch (err) {
            console.error(err);
            setCreateError('Failed to create page access. Please try again.');
        } finally {
            setIsCreating(false);
        }
    };

    const groupedAccesses = accesses.reduce((acc, access) => {
        const route = access.page_route;
        if (!acc[route]) {
            acc[route] = [];
        }
        acc[route].push(access);
        return acc;
    }, {});

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <div className="d-flex align-items-center justify-content-between">
                    <h6 className="text-lg fw-semibold mb-0">Role Page Access Management</h6>
                    <div className="d-flex align-items-center gap-3">
                        <select
                            className="form-select form-select-sm"
                            value={selectedRoleId}
                            onChange={(e) => setSelectedRoleId(e.target.value)}
                            style={{ width: '200px' }}
                        >
                            <option value="">Select Role...</option>
                            {roles.map((role) => (
                                <option key={role.id} value={role.id}>
                                    {role.name}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={() => {
                                setCreateFormData({
                                    role_id: selectedRoleId || '',
                                    page_route: '',
                                    menu_item: '',
                                    menu_label: '',
                                    can_access: true,
                                    show_in_menu: true,
                                    menu_order: 0,
                                });
                                setCreateError('');
                                setCreateValidationErrors({});
                                setShowCreateModal(true);
                            }}
                            className="btn btn-success btn-sm d-flex align-items-center gap-2"
                            disabled={!selectedRoleId}
                        >
                            <Icon icon="ic:baseline-plus" className="icon text-lg" />
                            Create Page
                        </button>
                        <button
                            type="button"
                            onClick={handleBulkUpdate}
                            className="btn btn-primary btn-sm"
                            disabled={isSaving || loading}
                        >
                            {isSaving ? 'Saving...' : 'Save All Changes'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="card-body p-24">
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className="table-responsive scroll-sm">
                        <table className="table bordered-table sm-table mb-0">
                            <thead>
                                <tr>
                                    <th scope="col" style={{ width: '30%' }}>Page Route</th>
                                    <th scope="col" style={{ width: '25%' }}>Menu Label</th>
                                    <th scope="col" className="text-center" style={{ width: '15%' }}>Can Access</th>
                                    <th scope="col" className="text-center" style={{ width: '15%' }}>Show in Menu</th>
                                    <th scope="col" className="text-center" style={{ width: '15%' }}>Menu Order</th>
                                </tr>
                            </thead>
                            <tbody>
                                {accesses.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="text-center py-4">
                                            No page access records found
                                        </td>
                                    </tr>
                                ) : (
                                    accesses
                                        .sort((a, b) => a.menu_order - b.menu_order)
                                        .map((access) => (
                                            <tr key={access.id}>
                                                <td>
                                                    <code className="text-sm">{access.page_route}</code>
                                                </td>
                                                <td>{access.menu_label || 'N/A'}</td>
                                                <td className="text-center">
                                                    <div className="form-check form-switch d-flex justify-content-center">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={access.can_access}
                                                            onChange={() => handleToggle(access.id, 'can_access', access.can_access)}
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="form-check form-switch d-flex justify-content-center">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={access.show_in_menu}
                                                            onChange={() => handleToggle(access.id, 'show_in_menu', access.show_in_menu)}
                                                            disabled={!access.can_access}
                                                            style={{ cursor: access.can_access ? 'pointer' : 'not-allowed' }}
                                                        />
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm text-center"
                                                        value={access.menu_order}
                                                        onChange={(e) => {
                                                            const newOrder = parseInt(e.target.value) || 0;
                                                            setAccesses(accesses.map(a => 
                                                                a.id === access.id 
                                                                    ? { ...a, menu_order: newOrder }
                                                                    : a
                                                            ));
                                                        }}
                                                        style={{ width: '80px', margin: '0 auto' }}
                                                    />
                                                </td>
                                            </tr>
                                        ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="mt-24 p-16 bg-info-50 radius-8">
                    <h6 className="text-sm fw-semibold mb-8">Instructions:</h6>
                    <ul className="text-sm mb-0" style={{ paddingLeft: '20px' }}>
                        <li><strong>Can Access:</strong> Controls whether users with this role can access the page. If disabled, users will be logged out if they try to access it.</li>
                        <li><strong>Show in Menu:</strong> Controls whether the menu item appears in the sidebar. This is only enabled if "Can Access" is enabled.</li>
                        <li><strong>Menu Order:</strong> Controls the display order of menu items. Lower numbers appear first.</li>
                        <li>Changes are saved immediately when toggling switches, or click "Save All Changes" to save menu order updates.</li>
                        <li>Click "Create Page" to add a new page access record for the selected role.</li>
                    </ul>
                </div>
            </div>

            {/* Create Page Modal */}
            {showCreateModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Create New Page Access</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowCreateModal(false)}
                                    disabled={isCreating}
                                />
                            </div>
                            <form onSubmit={handleCreateSubmit} noValidate>
                                <div className="modal-body">
                                    {createError && (
                                        <div className="alert alert-danger" role="alert">
                                            {createError}
                                        </div>
                                    )}
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">
                                                Role <span className="text-danger-600">*</span>
                                            </label>
                                            <select
                                                className={`form-select ${createValidationErrors.role_id ? 'is-invalid' : ''}`}
                                                name="role_id"
                                                value={createFormData.role_id}
                                                onChange={handleCreateInputChange}
                                                required
                                            >
                                                <option value="">Select Role...</option>
                                                {roles.map((role) => (
                                                    <option key={role.id} value={role.id}>
                                                        {role.name}
                                                    </option>
                                                ))}
                                            </select>
                                            {createValidationErrors.role_id && (
                                                <div className="invalid-feedback d-block">
                                                    {createValidationErrors.role_id[0]}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">
                                                Page Route <span className="text-danger-600">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control ${createValidationErrors.page_route ? 'is-invalid' : ''}`}
                                                name="page_route"
                                                value={createFormData.page_route}
                                                onChange={handleCreateInputChange}
                                                placeholder="e.g., /new-page, /settings"
                                                required
                                            />
                                            <small className="text-secondary-light">Enter the route path (e.g., /new-page)</small>
                                            {createValidationErrors.page_route && (
                                                <div className="invalid-feedback d-block">
                                                    {createValidationErrors.page_route[0]}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">
                                                Menu Label
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control ${createValidationErrors.menu_label ? 'is-invalid' : ''}`}
                                                name="menu_label"
                                                value={createFormData.menu_label}
                                                onChange={handleCreateInputChange}
                                                placeholder="e.g., New Page, Settings"
                                            />
                                            <small className="text-secondary-light">Display name in the menu</small>
                                            {createValidationErrors.menu_label && (
                                                <div className="invalid-feedback d-block">
                                                    {createValidationErrors.menu_label[0]}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">
                                                Menu Item
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control ${createValidationErrors.menu_item ? 'is-invalid' : ''}`}
                                                name="menu_item"
                                                value={createFormData.menu_item}
                                                onChange={handleCreateInputChange}
                                                placeholder="e.g., new-page, settings"
                                            />
                                            <small className="text-secondary-light">Menu item identifier (optional)</small>
                                            {createValidationErrors.menu_item && (
                                                <div className="invalid-feedback d-block">
                                                    {createValidationErrors.menu_item[0]}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">
                                                Menu Order
                                            </label>
                                            <input
                                                type="number"
                                                className={`form-control ${createValidationErrors.menu_order ? 'is-invalid' : ''}`}
                                                name="menu_order"
                                                value={createFormData.menu_order}
                                                onChange={handleCreateInputChange}
                                                min="0"
                                            />
                                            <small className="text-secondary-light">Lower numbers appear first in menu</small>
                                            {createValidationErrors.menu_order && (
                                                <div className="invalid-feedback d-block">
                                                    {createValidationErrors.menu_order[0]}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Access Settings</label>
                                            <div className="d-flex flex-column gap-2">
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        name="can_access"
                                                        checked={createFormData.can_access}
                                                        onChange={handleCreateInputChange}
                                                        id="createCanAccess"
                                                    />
                                                    <label className="form-check-label" htmlFor="createCanAccess">
                                                        Can Access
                                                    </label>
                                                </div>
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        name="show_in_menu"
                                                        checked={createFormData.show_in_menu}
                                                        onChange={handleCreateInputChange}
                                                        disabled={!createFormData.can_access}
                                                        id="createShowInMenu"
                                                    />
                                                    <label className="form-check-label" htmlFor="createShowInMenu">
                                                        Show in Menu
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowCreateModal(false)}
                                        disabled={isCreating}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={isCreating}
                                    >
                                        {isCreating ? 'Creating...' : 'Create Page Access'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RolePageAccessLayer;

